<template>
  <div :data-qa="`form-${id}--wrapper`">
    <ValidationProvider :name="id" rules="required|password" v-slot="{ errors }">
      <b-field
        custom-class="z-u-text-label"
        :label="labelText"
        :type="{ 'is-danger': errors[0] }"
        :data-qa="`form-${id}--label`">
        <div class="por-password--input-wrapper">
          <b-input
            v-model="value"
            :name="id"
            :id="id"
            type="password"
            @input="checkPassword"
            :placeholder="placeholderText"
            size="is-medium"
            data-hj-suppress
            password-reveal
            expanded
            :data-qa="`form-${id}--input`"/>
          <div
            v-if="errors[0]"
            :data-qa="`form-${id}--error-text`"
            class="por-input-error-text">
            {{ $t('common_validPassword_text') }}.
          </div>
          <ul
            class="por-password--requirements mt-3 pt-6 pr-6 pb-6 pl-6"
            :data-qa="`form-${id}--list`">
            <li aria-hidden="false" class="is-hidden-touch is-hidden-desktop">{{ $t('password_requirements_text') }}:</li>
            <li class="por-password--requirements-item">
              <span
                class="por-password--requirements-icon--green"
                :data-qa="`form-${id}--length-valid`"
                v-if="validPasswordLength"><span class="por-password--requirements-icon">✓</span> {{ $t('password_charLimit_text') }}</span>
              <span
                :data-qa="`form-${id}--length-invalid`"
                v-else><span class="por-password--requirements-icon">●</span> {{ $t('password_charLimit_text') }}</span>
            </li>
            <li class="por-password--requirements-item pt-3">
              <span
                class="por-password--requirements-icon--green"
                :data-qa="`form-${id}--int-valid`"
                v-if="validPasswordInt"><span class="por-password--requirements-icon">✓</span> {{ $t('password_reqDigit_placeholder') }}</span>
              <span
                :data-qa="`form-${id}--int-invalid`"
                v-else><span class="por-password--requirements-icon">●</span> {{ $t('password_reqDigit_placeholder') }}</span>
            </li>
            <li class="por-password--requirements-item pt-3">
              <span
                class="por-password--requirements-icon--green"
                :data-qa="`form-${id}--lowercase-valid`"
                v-if="validPasswordLowercase"><span class="por-password--requirements-icon">✓</span> {{ $t('password_reqLowerCase_placeholder') }}</span>
              <span
                :data-qa="`form-${id}--lowercase-invalid`"
                v-else><span class="por-password--requirements-icon">●</span> {{ $t('password_reqLowerCase_placeholder') }}</span>
            </li>
            <li class="por-password--requirements-item pt-3">
              <span
                class="por-password--requirements-icon--green"
                :data-qa="`form-${id}--uppercase-valid`"
                v-if="validPasswordUppercase"><span class="por-password--requirements-icon">✓</span> {{ $t('password_reqCapital_placeholder') }}</span>
              <span
                :data-qa="`form-${id}--uppercase-invalid`"
                v-else><span class="por-password--requirements-icon">●</span> {{ $t('password_reqCapital_placeholder') }}</span>
            </li>
          </ul>
        </div>
      </b-field>
    </ValidationProvider>
  </div>
</template>
<script>
import { validatePasswordInt, validatePasswordUppercase, validatePasswordLowercase, validatePasswordLength } from '@/services/validation/password'

export default {
  name: 'Password',
  props: {
    id: {
      type: String,
      required: true
    },
    inputModel: {
      type: String,
      required: true
    },
    inputValidation: {
      type: Function
    },
    labelText: {
      type: String,
      required: true
    },
    placeholderText: {
      type: String
    }
  },
  data () {
    return {
      hasBlurred: false,
      validPasswordLength: false,
      validPasswordUppercase: false,
      validPasswordLowercase: false,
      validPasswordInt: false,
      value: this.inputModel // Needed so we don't mutate a prop
    }
  },
  methods: {
    checkPassword (event, isBlur = false) {
      this.validPasswordLength = validatePasswordLength(this.value)
      this.validPasswordLowercase = validatePasswordLowercase(this.value)
      this.validPasswordUppercase = validatePasswordUppercase(this.value)
      this.validPasswordInt = validatePasswordInt(this.value)

      this.$emit('update:inputModel', this.value)

      this.hasBlurred = true
    }
  },
  mounted () {
    if (this.inputModel) { // Validate if field is auto-filled (for example a password manager)
      this.checkPassword()
    }
  }
}
</script>

<style lang="scss">
  $por-password--requirements-border-radius: 8px;
  $por-password--requirements-line-height: 1em;

  $por-password--requirements-icon-width: 30px;
  $por-password--requirements-padding-left: 0;

  .por-password--requirements {
    background: $white-ter;
    border-radius: $por-password--requirements-border-radius;
  }

  .por-password--requirements-icon {
    display: inline-block;
    width: $por-password--requirements-icon-width;
    padding-left: $por-password--requirements-padding-left;
  }

  .por-password--requirements-icon--green {
    color: $success;
    line-height: $por-password--requirements-line-height;
  }

  // Icon hack as icons aren't working with ui-packages yet
  // Review this when icons are working with ui-packages
  .mdi-eye {
    background-color: white;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="black" fill-opacity="0.72"/></svg>');
    height: 24px;
    width: 24px;
  }

  // We need different classes per brand
  // This should be handled automatically when icons are working
  .zava .mdi-eye-off {
    background-color: white;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="%230F62F4" fill-opacity="0.72"/></svg>');
    height: 24px;
    width: 24px;
  }

  .sd .mdi-eye-off {
    background-color: white;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="%23EB008B" fill-opacity="0.72"/></svg>');
    height: 24px;
    width: 24px;
  }

  .asda .mdi-eye-off {
    background-color: white;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="%230073B1" fill-opacity="1"/></svg>');
    height: 24px;
    width: 24px;
  }
</style>
